import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { authenticatedFetch } from "../../utils/api";
import Navbar from "../Navbar";
import Loader from "../Loader";

const FixedQuestions = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { showError, showSuccess, showWarning } = useToast();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editedQuestions, setEditedQuestions] = useState([]);

  // Available days for selection
  const availableDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
    "Everyday",
  ];

  // Available pillars (show all 7 pillars in dropdown)
  const availablePillars = [
    "Job Satisfaction",
    "Role Clarity",
    "Support & Supervision",
    "Belonging & Recognition",
    "Advocacy (Net Promoter Score)",
    "Upstart Experience",
    "Open Feedback",
  ];

  // Allowed question types
  // Allowed question types (only these five are permitted)
  const questionTypes = [
    { value: "rating", label: "Rating" },
    { value: "scale_labels", label: "Scale Labels" },
    { value: "text", label: "Text / Open" },
    { value: "multiple_choice", label: "Multiple Choice" },
    { value: "boolean", label: "Yes / No" },
  ];

  const normalizeQuestionType = (t) => {
    if (!t && t !== 0) return "text";
    const lower = String(t).toLowerCase();
    if (lower === "rating") return "rating";
    if (lower === "nps" || lower === "scale" || lower === "scale_labels" || lower === "scale-labels") return "scale_labels";
    if (lower === "multiple_choice" || lower === "multiplechoice" || lower === "mc") return "multiple_choice";
    if (lower === "boolean" || lower === "yesno" || lower === "yes/no") return "boolean";
    if (lower === "text") return "text";
    return "text";
  };
  // Convert API types to frontend internal types
  // (normalizeQuestionType already handles mapping from API values like 'scale' to 'scale_labels' and 'yes_no' to 'boolean')

  const optionsToStrings = (opts) => {
    if (!opts) return null;
    if (!Array.isArray(opts)) return null;
    return opts.map((o) => {
      if (typeof o === 'string') return o;
      if (o && typeof o === 'object') return o.text ?? o.value ?? JSON.stringify(o);
      return String(o);
    });
  };

  const mapToApiType = (t) => {
    const lower = String(t || '').toLowerCase();
    if (lower === 'rating') return 'rating';
    if (lower === 'scale_labels' || lower === 'scale-labels' || lower === 'scale') return 'scale';
    if (lower === 'multiple_choice' || lower === 'multiplechoice' || lower === 'mc') return 'multiple_choice';
    if (lower === 'boolean' || lower === 'yes_no' || lower === 'yes/no' || lower === 'yesno') return 'yes_no';
    if (lower === 'text') return 'text';
    return 'text';
  };
  // Fetch fixed questions from API
  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const url = "https://pulse.upstartaec.com/agent/survey-generation/fixed-questions";
      const response = await authenticatedFetch(url, {
        method: "GET",
      });

      if (response && (response.success || response.data)) {
        const questionsData = response.success ? response.data : (response.data || []);
        const normalized = Array.isArray(questionsData)
          ? questionsData.map((q) => ({
              id: q.id,
              question: q.question || "",
              pillar: q.pillar || "",
              // convert day string like "Monday, Wednesday" to array
              day: q.day ? q.day.split(",").map((d) => d.trim()) : [],
              question_type: normalizeQuestionType(q.question_type),
              required: q.required !== undefined ? !!q.required : (normalizeQuestionType(q.question_type) === 'boolean'),
              // normalize options from API (objects) to simple strings for UI
              options: optionsToStrings(q.options) || (normalizeQuestionType(q.question_type) === 'boolean' ? ["Yes", "No"] : null),
              min_value: q.min_value || null,
              max_value: q.max_value || null,
              scale_labels: q.scale_labels || [],
              placeholder: q.placeholder || "",
              default_answer: q.default_answer || (normalizeQuestionType(q.question_type) === 'boolean' ? 'Yes' : null),
            }))
          : [];

        setQuestions(normalized);
        setEditedQuestions(normalized.map((x) => ({ ...x })));
      } else {
        showError("Failed to fetch fixed questions. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching fixed questions:", error);
      if (error.message.includes("Authentication")) {
        showError("Authentication expired. Please log in again.");
        logout();
        navigate("/login");
      } else {
        showError(`Failed to fetch fixed questions: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [showError, logout, navigate]);

  // Fetch questions on component mount
  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  // Handle edit mode toggle (unused currently)

  // Check if there are any changes between original and edited questions
  const hasChanges = useMemo(() => {
    if (questions.length !== editedQuestions.length) {
      return true;
    }

    return questions.some((original, index) => {
      const edited = editedQuestions[index];
      if (!edited) return true;

      const dayOriginal = Array.isArray(original.day) ? original.day.join(", ") : original.day;
      const dayEdited = Array.isArray(edited.day) ? edited.day.join(", ") : edited.day;

      return (
        original.question !== edited.question ||
        original.pillar !== edited.pillar ||
        dayOriginal !== dayEdited ||
        (original.question_type || "") !== (edited.question_type || "") ||
        !!original.required !== !!edited.required ||
        JSON.stringify(original.options || null) !== JSON.stringify(edited.options || null) ||
        (original.min_value || null) !== (edited.min_value || null) ||
        (original.max_value || null) !== (edited.max_value || null) ||
        JSON.stringify(original.scale_labels || []) !== JSON.stringify(edited.scale_labels || []) ||
        (original.placeholder || "") !== (edited.placeholder || "")
      );
    });
  }, [questions, editedQuestions]);

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingId(null);
    // Reset edited questions to original without calling API
    // Create a deep copy to ensure proper reset
    setEditedQuestions(
      questions.map((q) => ({
        id: q.id,
        question: q.question,
        pillar: q.pillar,
        day: Array.isArray(q.day) ? [...q.day] : (q.day ? q.day.split(",").map(d=>d.trim()) : []),
        question_type: normalizeQuestionType(q.question_type || q.question_type),
        required: q.required !== undefined ? !!q.required : false,
        options: q.options || null,
        min_value: q.min_value || null,
        max_value: q.max_value || null,
        scale_labels: q.scale_labels || [],
        placeholder: q.placeholder || "",
        default_answer: q.default_answer || (normalizeQuestionType(q.question_type || q.question_type) === 'boolean' ? 'Yes' : null),
      }))
    );
  };

  // Handle input change for editing
  const handleInputChange = (questionId, field, value) => {
    setEditedQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId ? { ...q, [field]: value } : q
      )
    );
  };

  // Handle day selection toggle (multi-select via checkboxes)
  const handleDayToggle = (questionId, dayValue) => {
    setEditedQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== questionId) return q;
        const current = Array.isArray(q.day) ? [...q.day] : [];
        const idx = current.findIndex((d) => d === dayValue);
        if (idx > -1) {
          current.splice(idx, 1);
        } else {
          current.push(dayValue);
        }
        return { ...q, day: current };
      })
    );
  };

  // Note: boolean questions use `default_answer` (Yes/No) and always provide options ["Yes","No"]

  // Helper to convert comma-separated string to array
  const stringToArray = (val) => {
    if (!val && val !== 0) return [];
    if (Array.isArray(val)) return val;
    return String(val)
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  };

  // Save all edited questions
  const handleSaveAll = async () => {
    if (editedQuestions.length === 0) {
      showWarning("No questions to save.");
      return;
    }

    // Validate all questions have required fields and type-specific requirements
    const invalidQuestions = editedQuestions.filter((q) => {
      const dayArr = Array.isArray(q.day) ? q.day : stringToArray(q.day);
      if (!q.question || !q.pillar || dayArr.length === 0) return true;

      const type = q.question_type;
      if (type === "multiple_choice") {
        const opts = q.options ? (Array.isArray(q.options) ? q.options : stringToArray(q.options)) : [];
        if (opts.length === 0) return true;
      }

      if (type === "rating") {
        // rating requires min and max
        if (q.min_value === null || q.min_value === undefined || q.min_value === "") return true;
        if (q.max_value === null || q.max_value === undefined || q.max_value === "") return true;
      }

      if (type === "scale_labels") {
        const labels = q.scale_labels ? (Array.isArray(q.scale_labels) ? q.scale_labels : stringToArray(q.scale_labels)) : [];
        if (labels.length === 0) return true;
      }

      return false;
    });

    if (invalidQuestions.length > 0) {
      showWarning("Please fill in all required fields (question, pillar, day) for all questions.");
      return;
    }

    setSaving(true);
    try {
      const url = "https://pulse.upstartaec.com/agent/survey-generation/fixed-questions";
      const payload = {
        questions: editedQuestions.map((q) => {
          // normalize options / scale labels based on type
          let optionsVal = null;
          if (q.question_type === "boolean") {
            // send as list of option strings first
            optionsVal = ["Yes", "No"];
          } else if (q.question_type === "multiple_choice") {
            optionsVal = q.options
              ? (Array.isArray(q.options) ? q.options : stringToArray(q.options))
              : [];
          }

          const scaleLabelsVal = q.scale_labels
            ? (Array.isArray(q.scale_labels) ? q.scale_labels : stringToArray(q.scale_labels))
            : null;

          // convert simple option strings to API objects {text, value}
          let optionsForApi = null;
          if (Array.isArray(optionsVal) && optionsVal.length > 0) {
            optionsForApi = optionsVal.map((o) => {
              if (typeof o === 'string') return { text: o, value: o };
              if (o && typeof o === 'object') return o;
              return { text: String(o), value: String(o) };
            });
          }

          return {
            id: q.id,
            question: q.question,
            pillar: q.pillar,
            day: Array.isArray(q.day) ? q.day.join(", ") : (q.day || ""),
            question_type: mapToApiType(q.question_type || q.question_type),
            required: !!q.required,
            options: optionsForApi && optionsForApi.length > 0 ? optionsForApi : null,
            min_value: q.min_value !== undefined && q.min_value !== null && q.min_value !== "" ? Number(q.min_value) : null,
            max_value: q.max_value !== undefined && q.max_value !== null && q.max_value !== "" ? Number(q.max_value) : null,
            scale_labels: scaleLabelsVal && scaleLabelsVal.length > 0 ? scaleLabelsVal : null,
            placeholder: q.placeholder || null,
            default_answer: q.default_answer || null,
          };
        }),
      };
      const response = await authenticatedFetch(url, {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      if (response && (response.success || response.data)) {
        // Re-fetch the questions from the server to ensure FE reflects server state
        await fetchQuestions();
        setEditingId(null);
        const updatedQuestions = response.success ? response.data : (response.data || []);
        showSuccess(
          response.message || `Successfully updated ${updatedQuestions.length} fixed question(s)`
        );
      } else {
        showError("Failed to update fixed questions. Please try again.");
      }
    } catch (error) {
      console.error("Error updating fixed questions:", error);
      if (error.message.includes("Authentication")) {
        showError("Authentication expired. Please log in again.");
        logout();
        navigate("/login");
      } else {
        showError(`Failed to update fixed questions: ${error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Fixed Questions
                  </h1>
                  <p className="mt-1 text-sm text-gray-600">
                    Manage and edit fixed survey questions
                  </p>
                </div>
                {editingId === null && (
                  <button
                    onClick={() => setEditingId("all")}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit Questions
                  </button>
                )}
                {editingId === "all" && (
                  <div className="flex gap-2">
                    <button
                      onClick={handleCancelEdit}
                      className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveAll}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={saving || !hasChanges}
                    >
                      {saving ? (
                        <>
                          <svg
                            className="animate-spin h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Saving...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          Save All
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Questions List */}
            <div className="p-6">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader />
                </div>
              ) : editedQuestions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <svg
                    className="w-16 h-16 mx-auto mb-4 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-lg">No fixed questions found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {editedQuestions.map((question, index) => (
                    <div
                      key={question.id}
                      className="bg-gray-50 rounded-lg p-6 border border-gray-200"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                            {index + 1}
                          </span>
                        </div>
                        <div className="flex-1 space-y-4">
                          {/* Question Field */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Question *
                            </label>
                            {editingId === "all" ? (
                              <textarea
                                value={question.question || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    question.id,
                                    "question",
                                    e.target.value
                                  )
                                }
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical min-h-[80px]"
                                placeholder="Enter question text..."
                              />
                            ) : (
                              <p className="text-gray-900 text-base">
                                {question.question}
                              </p>
                            )}
                          </div>

                          {/* Pillar and Day Fields */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Pillar *
                              </label>
                              {editingId === "all" ? (
                                <select
                                  value={question.pillar || ""}
                                  onChange={(e) =>
                                    handleInputChange(question.id, "pillar", e.target.value)
                                  }
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                >
                                  <option value="">Select pillar</option>
                                  {availablePillars.map((p) => (
                                    <option key={p} value={p}>
                                      {p}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <p className="text-gray-700">{question.pillar}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Day *
                              </label>
                              {editingId === "all" ? (
                                <div className="flex flex-wrap gap-2">
                                  {availableDays.filter(d => d !== 'Saturday' && d !== 'Sunday').map((day) => {
                                    const checked = Array.isArray(question.day)
                                      ? question.day.includes(day)
                                      : String(question.day || "").split(",").map(s=>s.trim()).includes(day);
                                    return (
                                      <label key={day} className="inline-flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() => handleDayToggle(question.id, day)}
                                          className="form-checkbox h-4 w-4 text-blue-600"
                                        />
                                        <span className="text-sm text-gray-700">{day}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="text-gray-700">
                                  {Array.isArray(question.day) ? question.day.join(", ") : question.day}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Question type and follow-up details */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Question Type</label>
                              {editingId === "all" ? (
                                <select
                                  value={question.question_type || ""}
                                  onChange={(e) => handleInputChange(question.id, "question_type", e.target.value)}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                >
                                  <option value="">Select type</option>
                                  {questionTypes.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                  ))}
                                </select>
                              ) : (
                                <p className="text-gray-700">{question.question_type || '-'}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Required</label>
                              {editingId === "all" ? (
                                <label className="inline-flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={!!question.required}
                                    onChange={() => handleInputChange(question.id, "required", !question.required)}
                                    className="form-checkbox h-4 w-4 text-blue-600"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">Required</span>
                                </label>
                              ) : (
                                <p className="text-gray-700">{question.required ? 'Yes' : 'No'}</p>
                              )}
                            </div>
                          </div>

                          <div className="mt-3 space-y-3">
                            {/* Options / Scale / Min value / Placeholder — show conditionally by question type */}
                            {question.question_type === "multiple_choice" && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Options (comma separated)</label>
                                {editingId === "all" ? (
                                  <input
                                    type="text"
                                    value={Array.isArray(question.options) ? question.options.join(", ") : (question.options || "")}
                                    onChange={(e) => handleInputChange(question.id, "options", e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="e.g., Option1, Option2"
                                  />
                                ) : (
                                  <p className="text-gray-700">{Array.isArray(question.options) ? question.options.join(', ') : (question.options || '-')}</p>
                                )}
                              </div>
                            )}

                              {question.question_type === "rating" && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Min value</label>
                                    {editingId === "all" ? (
                                      <input
                                        type="number"
                                        value={question.min_value || ''}
                                        onChange={(e) => handleInputChange(question.id, "min_value", e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="e.g., 1"
                                      />
                                    ) : (
                                      <p className="text-gray-700">{question.min_value ?? '-'}</p>
                                    )}
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Max value</label>
                                    {editingId === "all" ? (
                                      <input
                                        type="number"
                                        value={question.max_value || ''}
                                        onChange={(e) => handleInputChange(question.id, "max_value", e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="e.g., 5"
                                      />
                                    ) : (
                                      <p className="text-gray-700">{question.max_value ?? '-'}</p>
                                    )}
                                  </div>

                                  <div className="md:col-span-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">(rating uses min & max)</label>
                                  </div>
                                </div>
                              )}

                              {question.question_type === "scale_labels" && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">Scale labels (comma separated)</label>
                                  {editingId === "all" ? (
                                    <input
                                      type="text"
                                      value={Array.isArray(question.scale_labels) ? question.scale_labels.join(', ') : (question.scale_labels || '')}
                                      onChange={(e) => handleInputChange(question.id, "scale_labels", e.target.value)}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      placeholder="e.g., Very dissatisfied, Dissatisfied, Neutral"
                                    />
                                  ) : (
                                    <p className="text-gray-700">{Array.isArray(question.scale_labels) ? question.scale_labels.join(', ') : (question.scale_labels || '-')}</p>
                                  )}
                                </div>
                              )}

                            {question.question_type === "boolean" && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Default Answer</label>
                                {editingId === "all" ? (
                                  <select
                                    value={question.default_answer || 'Yes'}
                                    onChange={(e) => handleInputChange(question.id, 'default_answer', e.target.value)}
                                    className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                  >
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                  </select>
                                ) : (
                                  <p className="text-gray-700">{question.default_answer || 'Yes'}</p>
                                )}
                              </div>
                            )}

                            {question.question_type === "text" && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Placeholder</label>
                                {editingId === "all" ? (
                                  <input
                                    type="text"
                                    value={question.placeholder || ''}
                                    onChange={(e) => handleInputChange(question.id, "placeholder", e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Placeholder text for open answers"
                                  />
                                ) : (
                                  <p className="text-gray-700">{question.placeholder || '-'}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FixedQuestions;

